import React from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import "./UrbanGovernance.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

/* ---------------- Inline icon set (no external icon package needed) ---------------- */

const IconCity = ({ color = "#ffffff" }) => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 42V18l10-6 10 6v24" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    <path d="M26 42V10l8-4 8 4v32" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    <path d="M2 42h44" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M11 24h2M11 30h2M11 36h2M19 24h2M19 30h2M19 36h2" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
    <path d="M31 16h2M31 22h2M31 28h2M31 34h2M38 16h2M38 22h2M38 28h2M38 34h2" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const IconTraining = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="22" y="8" width="18" height="13" rx="1.5" stroke="#1a6b3c" strokeWidth="2" />
    <path d="M26 14h10M26 17h6" stroke="#1a6b3c" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="16" r="4.2" stroke="#1a6b3c" strokeWidth="2" />
    <path d="M6 34c0-6 3.5-9.5 9-9.5s9 3.5 9 9.5" stroke="#1a6b3c" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconEye = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 24s7-13 20-13 20 13 20 13-7 13-20 13-20-13-20-13z" stroke="#1a6b3c" strokeWidth="2" strokeLinejoin="round" />
    <circle cx="24" cy="24" r="6" stroke="#1a6b3c" strokeWidth="2" />
  </svg>
);

const IconGroup = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="14" r="5" stroke="#1a6b3c" strokeWidth="2" />
    <circle cx="12" cy="19" r="4" stroke="#1a6b3c" strokeWidth="2" />
    <circle cx="36" cy="19" r="4" stroke="#1a6b3c" strokeWidth="2" />
    <path d="M14 34c0-6.5 4.5-10.5 10-10.5s10 4 10 10.5" stroke="#1a6b3c" strokeWidth="2" strokeLinecap="round" />
    <path d="M6 32c.6-4.5 3-7 6.5-7.6M42 32c-.6-4.5-3-7-6.5-7.6" stroke="#1a6b3c" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconMegaphone = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 20v8a2 2 0 002 2h3l14 6V12l-14 6H10a2 2 0 00-2 2z" stroke="#1a6b3c" strokeWidth="2" strokeLinejoin="round" />
    <path d="M27 18a6 6 0 010 12" stroke="#1a6b3c" strokeWidth="2" strokeLinecap="round" />
    <path d="M13 30v6a2 2 0 002 2h1a2 2 0 002-2v-4" stroke="#1a6b3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconDocument = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 6h16l8 8v28a2 2 0 01-2 2H12a2 2 0 01-2-2V8a2 2 0 012-2z" stroke="#1a6b3c" strokeWidth="2" strokeLinejoin="round" />
    <path d="M28 6v8h8" stroke="#1a6b3c" strokeWidth="2" strokeLinejoin="round" />
    <path d="M16 24h16M16 30h16M16 36h10" stroke="#1a6b3c" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconHandsHeart = ({ color = "#ef7f3a" }) => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 26c-3-2.8-5.2-5.4-5.2-8.5a4.5 4.5 0 019-1 4.5 4.5 0 019 1c0 3.1-2.2 5.7-5.2 8.5l-3.8 3.6-3.8-3.6z"
      stroke={color}
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M9 30c-2 2.2-3.5 4.8-3.5 8 0 4 3.3 7 7.3 7" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M39 30c2 2.2 3.5 4.8 3.5 8 0 4-3.3 7-7.3 7" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconHeart = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21s-7.5-4.6-10-9.3C.4 8.3 2 4.5 5.6 4c2.1-.3 4 .8 6.4 3 2.4-2.2 4.3-3.3 6.4-3 3.6.5 5.2 4.3 3.6 7.7C19.5 16.4 12 21 12 21z" />
  </svg>
);

const IconUsers = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M2.8 20c.7-3.6 3.3-5.6 6.2-5.6s5.5 2 6.2 5.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M15.5 5.4a3.2 3.2 0 010 6.2M18.8 20c-.5-2.6-1.9-4.4-3.8-5.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const IconHandHeart = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 38c-4-4-7-8-7-13a7 7 0 0114-1 7 7 0 0114 1c0 5-3 9-7 13l-7 7-7-7z"
      stroke="#ffffff"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <path
      d="M14 34c-3 3-6 7-6 12 0 5 4 9 9 9h9c4 0 7-2 10-5l9-9c1.5-1.5 1.5-3.7 0-5-1.4-1.3-3.4-1.2-4.8.1l-6.2 6"
      fill="#ef7f3a"
    />
  </svg>
);

/* ------------------------------------------------------------------------------------ */

const UrbanGovernance = () => {
  const initiatives = [
    {
      title: "Capacity Building",
      text: "Capacity building for elected representatives of Urban Local Bodies.",
      icon: <IconTraining />,
    },
    {
      title: "Exposure Visits",
      text: "Exposure visits to model ULBs for enhanced learning and best practices.",
      icon: <IconEye />,
    },
    {
      title: "Citizen Group Formation",
      text: "Formation of citizen groups (Youth, Women, Media) to strengthen services.",
      icon: <IconGroup />,
    },
    {
      title: "Citizen Involvement Campaigns",
      text: "Campaigns to increase citizen involvement and participation in urban governance.",
      icon: <IconMegaphone />,
    },
    {
      title: "Research & Studies",
      text: "Studies on the decentralization of urban governance in Odisha for evidence-based solutions.",
      icon: <IconDocument />,
    },
  ];

  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="ug-hero">
        <div
          className="ug-hero__bg"
          style={{
            backgroundImage: "url(/assets/images/backgrounds/Urban.png)",
          }}
        ></div>
        <div className="ug-hero__overlay"></div>
        <div className="ug-hero__dots"></div>

        <div className="ug-hero__logo">
          <IconHandHeart />
        </div>

        <svg className="ug-hero__wave" viewBox="0 0 200 260" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2 2c30 20-20 45 10 65s45-10 60 15-10 40 20 60"
            stroke="#ef7f3a"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>

        <Container className="ug-hero__content">
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Urban
            <br />
            <span>Governance</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Vikash promotes urban governance by encouraging transparency,
            accountability, and community involvement in city planning
            and decision-making.
          </motion.p>
        </Container>
      </section>

      {/* Introduction Section */}
      <section className="ug-intro">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-center g-5">
              <motion.div className="col-lg-6" variants={staggerItem}>
                <div className="ug-eyebrow">Our Mission</div>
                <span className="ug-eyebrow__dash"></span>
                <h2 className="ug-heading">
                  Building Better
                  <br />
                  Cities Together
                </h2>
                <p className="ug-text">
                  Through regional consultations and in-depth studies,
                  Vikash identifies critical barriers to effective urban
                  governance. We facilitate the formation of diverse
                  citizen groups, including youth, senior citizens, and
                  women to strengthen public infrastructure and services.
                  By organizing capacity-building programs and exposure
                  visits for elected representatives, we empower local
                  leaders and campaign for greater citizen involvement.
                </p>
              </motion.div>

              <motion.div className="col-lg-6" variants={staggerItem}>
                <div className="ug-intro__imgwrap">
                  <div className="ug-intro__dots"></div>
                  <div className="ug-intro__accent"></div>
                  <img
                    src="/assets/images/Subsections/ug1.jpg"
                    alt="Urban governance meeting"
                    className="ug-intro__img"
                  />
                </div>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Core Programs Section */}
      <section className="ug-programs">
        <div className="ug-programs__leaf">
          <svg viewBox="0 0 120 160" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M60 150V40" stroke="#cddccf" strokeWidth="3" />
            <path d="M60 60c-15-15-35-15-45-5 10 15 30 15 45 5z" fill="#cddccf" />
            <path d="M60 90c15-15 35-15 45-5-10 15-30 15-45 5z" fill="#cddccf" />
            <path d="M60 120c-12-12-28-12-36-4 8 12 24 12 36 4z" fill="#cddccf" />
          </svg>
        </div>

        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="text-center ug-programs__head"
          >
            <div className="ug-programs__icon">
              <IconCity color="#1a6b3c" />
            </div>
            <h2 className="ug-heading ug-heading--center">
              Our Core Urban Governance Programs
            </h2>
            <span className="ug-underline ug-underline--center"></span>
            <p className="ug-subtext">
              We empower both citizens and leaders to create more
              responsive and effective city management.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {initiatives.map((item, index) => (
                <Col md={6} lg={4} key={index}>
                  <motion.div variants={staggerItem} className="h-100">
                    <Card className="ug-card h-100">
                      <Card.Body>
                        <div className="ug-card__icon">{item.icon}</div>
                        <Card.Title as="h5">{item.title}</Card.Title>
                        <span className="ug-card__underline"></span>
                        <Card.Text>{item.text}</Card.Text>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Call To Action Section */}
      <section className="ug-cta">
        <Container>
          <motion.div
            className="ug-cta__panel"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <Row className="align-items-center g-0">
              <Col lg={6} className="ug-cta__left">
                <div className="ug-cta__icon">
                  <IconHandsHeart />
                </div>
                <h2 className="ug-cta__title">
                  Support Transparent
                  <br />
                  <span>Governance</span>
                </h2>
                <span className="ug-underline"></span>
                <p className="ug-cta__text">
                  Your contribution helps us train local leaders and
                  promote active citizen participation in our cities.
                </p>
                <div className="ug-cta__actions">
                  <Button className="ug-btn ug-btn--solid" onClick={openDonation}>
                    <IconHeart /> Donate Now
                  </Button>
                  <Button
                    className="ug-btn ug-btn--outline"
                    onClick={() => navigate("/interns")}
                  >
                    <IconUsers /> Join Us
                  </Button>
                </div>
              </Col>

              <Col lg={6} className="ug-cta__right">
                <img
                  src="/assets/images/backgrounds/do5.png"
                  alt="Community members in a city park discussion"
                  className="ug-cta__img"
                />
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default UrbanGovernance;